import {
  type ApplicationConfig,
  LOCALE_ID,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withViewTransitions } from '@angular/router';
import { appRoutes } from './app.routes';
import {
  httpErrorInterceptor,
  I18nProviderInjectionToken as I18nProviderInjectionTokenError,
  provideHttpErrorOptions,
} from '@pct-ng/core/error-http';
import {
  appInitializerPreloadScopes,
  TRANSLOCO_LOADER_LOAD_EXTRA_NAMESPACE,
  translocoProvider,
} from '@pct-ng/transloco';
import { I18nProviderInjectionToken as I18nProviderInjectionTokenCore } from '@pct-ng/i18n';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { environment } from '@szte-griffin/environment';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { provideStore } from '@ngxs/store';
import { AppState } from '@pct-ng/feature-app-layout-state';
import { TableState } from '@pct-ng/feature-table-material';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import {
  MAIN_MENU_CONFIG_TOKEN,
  TOP_RIGHT_MENU_CONFIG_TOKEN,
} from '@szte-griffin/feature/app-layout/ui';
import { ErrorStateMatcher, MAT_DATE_LOCALE } from '@angular/material/core';
import { CustomErrorStateMatcher } from '@pct-ng/form';
import { defaultMatDialogProvider } from '@pct-ng/feature-message-material';
import { provideToastr } from 'ngx-toastr';
import { hu } from 'date-fns/locale';
import {
  ABSTRACT_DETAILS_FORM_REGISTER_FIELD_ERRORS_RESOLVER,
  CATEGORY_NAME_MAX_LENGTH_INJECTION_TOKEN,
  provideCustomDateFnsAdapter,
} from '@cappus/shared-common';
import { transformHttpErrorToPctHttpErrorResponse } from './core/error/http/transform-http-error-to-pct-http-error-response';
import { handleIdentifiedError } from './core/error/http/handle-identified-error';
import { MAIN_MENU_CONFIG } from './app-main-menu.config';
import { abstractDetailsFormRegisterFieldErrorsResolver } from './core/form/abstract-details-form-register-field-errors-resolver';
import { AUTH_SERVICE_INJECTION_TOKEN, provideKeycloakAngular } from '@szte-griffin/core/auth/ui';
import { includeBearerTokenInterceptor } from 'keycloak-angular';
import { MecapAuthService } from './core/auth/mecap-auth.service';
import { TOP_RIGHT_MENU_CONFIG } from './top-right-menu.config';
import {
  CATEGORY_NAME_MAX_LENGTH,
  mecapCoverImageConfig,
  mecapGalleryImageConfig,
} from './core/constant/constants';
import { COVER_IMAGE_CONFIG_INJECTION_TOKEN } from '@szte-griffin/cover-image';
import { GALLERY_IMAGE_CONFIG_INJECTION_TOKEN } from '@szte-griffin/gallery-control';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    {
      provide: AUTH_SERVICE_INJECTION_TOKEN,
      useExisting: MecapAuthService,
    },
    provideHttpErrorOptions({
      transformHttpErrorToPtHttpErrorResponseFn: transformHttpErrorToPctHttpErrorResponse,
      handleIdentifiedErrorFn: handleIdentifiedError,
    }),
    provideKeycloakAngular(),
    appInitializerPreloadScopes(
      'hu',
      I18nProviderInjectionTokenCore.scope,
      I18nProviderInjectionTokenError.scope,
    ),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes, withViewTransitions()),
    provideHttpClient(withInterceptors([includeBearerTokenInterceptor, httpErrorInterceptor])),
    translocoProvider(!environment.isAngularDevMode),
    provideTranslocoMessageformat(),
    provideStore(
      [AppState, TableState],
      { developmentMode: environment.isAngularDevMode },
      ...(environment.isAngularDevMode ? [withNgxsReduxDevtoolsPlugin()] : []),
    ),
    {
      provide: MAIN_MENU_CONFIG_TOKEN,
      useValue: MAIN_MENU_CONFIG,
    },
    {
      provide: TOP_RIGHT_MENU_CONFIG_TOKEN,
      useValue: TOP_RIGHT_MENU_CONFIG,
    },
    { provide: ErrorStateMatcher, useClass: CustomErrorStateMatcher },
    defaultMatDialogProvider,
    provideToastr({
      tapToDismiss: true,
      closeButton: true,
      preventDuplicates: true,
      countDuplicates: true,
      resetTimeoutOnDuplicate: true,
      includeTitleDuplicates: true,
      newestOnTop: true,
      progressBar: true,
      progressAnimation: 'decreasing',
      timeOut: 1000 * 10,
      positionClass: 'toast-top-right',
    }),
    {
      provide: LOCALE_ID,
      useValue: 'hu-HU',
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: hu,
    },
    provideCustomDateFnsAdapter(),
    {
      provide: ABSTRACT_DETAILS_FORM_REGISTER_FIELD_ERRORS_RESOLVER,
      useValue: abstractDetailsFormRegisterFieldErrorsResolver,
    },
    {
      provide: TRANSLOCO_LOADER_LOAD_EXTRA_NAMESPACE,
      useValue: ['common'],
    },
    {
      provide: CATEGORY_NAME_MAX_LENGTH_INJECTION_TOKEN,
      useValue: CATEGORY_NAME_MAX_LENGTH,
    },
    {
      provide: COVER_IMAGE_CONFIG_INJECTION_TOKEN,
      useValue: mecapCoverImageConfig,
    },
    {
      provide: GALLERY_IMAGE_CONFIG_INJECTION_TOKEN,
      useValue: mecapGalleryImageConfig,
    },
  ],
};
